class HydrationCalculator {
    constructor() {
        this.form = document.getElementById('hydrationForm');
        this.resultsSection = document.getElementById('results');
        this.recommendationEl = document.getElementById('recommendation');
        this.scheduleEl = document.getElementById('schedule');
        this.tipsEl = document.getElementById('tips');
        this.hydrationChart = null;
        this.scheduleChart = null;

        this.initialize();
    }

    initialize() {
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));

        // Add input validation
        const numberInputs = this.form.querySelectorAll('input[type="number"]');
        numberInputs.forEach(input => {
            input.addEventListener('input', () => this.validateInput(input));
        });
    }

    calculateHydration(weight, age, gender, activityLevel, climate, healthGoals, currentIntake) {
        try {
            // Parse age from the age range selected (e.g., "18-24" -> 21 as average)
            const ageNum = parseInt(age.split('-')[0]);

            // Base calculation using weight (in pounds)
            let baseWater = parseFloat(weight) * 0.5; // Half your weight in ounces

            // Activity level adjustments
            const activityMultipliers = {
                'sedentary': 1.0,
                'moderate': 1.2,
                'very_active': 1.4,
                'athlete': 1.6
            };

            // Climate adjustments
            const climateAdditions = {
                'cold': 0,
                'temperate': 8,
                'hot': 16
            };

            // Calculate total water needs
            let totalWater = baseWater * (activityMultipliers[activityLevel] || 1.0);
            totalWater += climateAdditions[climate] || 0;

            // Convert to liters (1 oz = 0.0295735 L)
            const totalLiters = Math.round(totalWater * 0.0295735 * 100) / 100;

            return {
                ounces: Math.round(totalWater * 10) / 10,
                liters: totalLiters,
                cups: Math.round(totalWater / 8 * 10) / 10
            };
        } catch (error) {
            console.error("Error calculating hydration:", error);
            throw error;
        }
    }

    generateSchedule() {
        return [
            { time: "7:00 AM", amount: "16 oz", note: "Start your day" },
            { time: "9:00 AM", amount: "16 oz", note: "Mid-morning" },
            { time: "12:00 PM", amount: "16 oz", note: "With lunch" },
            { time: "3:00 PM", amount: "16 oz", note: "Afternoon" },
            { time: "6:00 PM", amount: "16 oz", note: "Evening" },
            { time: "8:00 PM", amount: "8 oz", note: "Before bed" }
        ];
    }

    generateTips(healthGoals) {
        const commonTips = [
            "Carry a reusable water bottle",
            "Set reminders on your phone",
            "Drink a glass of water before each meal"
        ];

        const goalSpecificTips = {
            'weight_loss': "Drink water before meals to help with portion control",
            'energy': "Stay hydrated to maintain energy levels throughout the day",
            'skin_health': "Proper hydration helps maintain skin elasticity and glow",
            'general': "Add fruit slices for natural flavor"
        };

        return [...commonTips, goalSpecificTips[healthGoals] || goalSpecificTips.general];
    }

    validateInput(input) {
        const value = parseFloat(input.value);
        const min = parseFloat(input.getAttribute('min'));
        const max = parseFloat(input.getAttribute('max'));

        let errorMessage = '';
        if (value < min) {
            errorMessage = `Value must be at least ${min}`;
        } else if (value > max) {
            errorMessage = `Value must be no more than ${max}`;
        }

        this.showInputError(input, errorMessage);
        return errorMessage === '';
    }

    showInputError(input, message) {
        const errorDiv = input.parentElement.querySelector('.error');
        if (message) {
            if (!errorDiv) {
                const div = document.createElement('div');
                div.className = 'error';
                div.textContent = message;
                input.parentElement.appendChild(div);
            } else {
                errorDiv.textContent = message;
            }
        } else if (errorDiv) {
            errorDiv.remove();
        }
    }

    createHydrationChart(data) {
        const ctx = document.getElementById('hydrationChart').getContext('2d');

        if (this.hydrationChart) {
            this.hydrationChart.destroy();
        }

        const currentIntake = parseFloat(this.form.current_intake.value) || 0;

        this.hydrationChart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: ['Recommended', 'Current Intake'],
                datasets: [{
                    label: 'Daily Water Intake (cups)',
                    data: [data.cups, currentIntake],
                    backgroundColor: [
                        'rgba(0, 80, 157, 0.8)',  // --primary-color
                        'rgba(253, 197, 0, 0.8)'  // --accent-color
                    ],
                    borderColor: [
                        'rgba(0, 80, 157, 1)',
                        'rgba(253, 197, 0, 1)'
                    ],
                    borderWidth: 2,
                    borderRadius: 6
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Cups of Water',
                            color: '#333',
                            font: {
                                weight: '600'
                            }
                        },
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        }
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: 'Daily Hydration Comparison',
                        color: '#00509d',
                        font: {
                            size: 16,
                            weight: '600'
                        },
                        padding: 20
                    },
                    legend: {
                        display: false
                    }
                }
            }
        });
    }

    createScheduleChart(schedule) {
        const ctx = document.getElementById('scheduleChart').getContext('2d');

        if (this.scheduleChart) {
            this.scheduleChart.destroy();
        }

        const times = schedule.map(item => item.time);
        const amounts = schedule.map(item => parseInt(item.amount));

        this.scheduleChart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: times,
                datasets: [{
                    label: 'Recommended Water Intake',
                    data: amounts,
                    fill: true,
                    borderColor: 'rgba(0, 80, 157, 1)',
                    backgroundColor: 'rgba(0, 80, 157, 0.1)',
                    tension: 0.4,
                    borderWidth: 3,
                    pointBackgroundColor: 'rgba(0, 80, 157, 1)',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Ounces of Water',
                            color: '#333',
                            font: {
                                weight: '600'
                            }
                        },
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        }
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: 'Daily Hydration Schedule',
                        color: '#00509d',
                        font: {
                            size: 16,
                            weight: '600'
                        },
                        padding: 20
                    },
                    legend: {
                        display: false
                    }
                }
            }
        });
    }

    handleSubmit(e) {
        e.preventDefault();

        // Validate all inputs
        const numberInputs = this.form.querySelectorAll('input[type="number"]');
        let isValid = true;
        numberInputs.forEach(input => {
            if (!this.validateInput(input)) {
                isValid = false;
            }
        });

        if (!isValid) return;

        try {
            const formData = {
                weight: parseFloat(this.form.weight.value),
                age: this.form.age.value,
                gender: this.form.gender.value,
                activity_level: this.form.activity_level.value,
                climate: this.form.climate.value,
                health_goals: this.form.health_goals.value,
                current_intake: this.form.current_intake.value ? parseFloat(this.form.current_intake.value) : null
            };

            const result = this.calculateHydration(
                formData.weight,
                formData.age,
                formData.gender,
                formData.activity_level,
                formData.climate,
                formData.health_goals,
                formData.current_intake
            );

            const schedule = this.generateSchedule();
            const tips = this.generateTips(formData.health_goals);

            this.displayResults({
                success: true,
                recommendation: result,
                schedule: schedule,
                tips: tips
            });
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while calculating your hydration needs. Please check your inputs and try again.');
        }
    }

    displayResults(data) {
        if (!data.success) {
            alert('Unable to calculate hydration needs. Please check your inputs.');
            return;
        }

        // Display recommendation
        this.recommendationEl.textContent =
            `Your daily water intake should be: ${data.recommendation.cups} cups (${data.recommendation.liters} liters)`;

        // Create/update charts
        this.createHydrationChart(data.recommendation);
        this.createScheduleChart(data.schedule);

        // Display schedule with headers
        this.scheduleEl.innerHTML = `
            <div class="schedule-header">
                <span>Time</span>
                <span>Amount</span>
                <span>Note</span>
            </div>
            ${data.schedule.map(item => `
                <div class="schedule-item">
                    <span>${item.time}</span>
                    <span>${item.amount}</span>
                    <span>${item.note}</span>
                </div>
            `).join('')}
        `;

        // Display tips
        this.tipsEl.innerHTML = `
            <h3>Hydration Tips</h3>
            <ul>
                ${data.tips.map(tip => `<li>${tip}</li>`).join('')}
            </ul>
        `;

        // Show results section with animation
        this.resultsSection.classList.add('active');
        this.resultsSection.scrollIntoView({ behavior: 'smooth' });
    }
}

// Initialize calculator when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new HydrationCalculator();
});